import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { ChapterRail, ChapterNav } from "@/components/WorkNav";
import { Reveal } from "../components/Reveal";
import { Logo } from "../components/Logo";

const KK = "/work/kk";

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/10">
    <div className="mx-auto max-w-[1500px] px-5 sm:px-8 h-16 flex items-center justify-between">
      <Logo imgClassName="h-9 sm:h-10" />
      <div className="flex items-center gap-4 sm:gap-6">
        <Link to="/work" data-testid="kotak-back-work-top" className="text-[11px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors">
          ← Work
        </Link>
        <Link to="/#gateways" data-testid="kotak-back-explore-top" className="text-[11px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors hidden sm:inline">
          Explore
        </Link>
      </div>
    </div>
  </header>
);

const Eyebrow = ({ children, testid }) => (
  <span data-testid={testid} className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#e62429] font-bold">
    {children}
  </span>
);

const Chip = ({ src, alt, className = "" }) => (
  <div className={`bg-white p-4 sm:p-5 border border-white/10 flex items-center justify-center ${className}`}>
    <img src={src} alt={alt} loading="lazy" className="max-h-full w-auto object-contain" />
  </div>
);

export default function Kotak() {
  const { scrollYProgress } = useScroll();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="kotak-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-0 left-0 right-0 h-[2px] bg-[#e62429] origin-left z-[60]" />
      <Header />
      <ChapterRail currentKey="kotak" />

      {/* OPENER — D21/D22 */}
      <section data-testid="kotak-opener" className="relative isolate min-h-[92svh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={`${KK}/night1.jpg`} alt="Night cricket under lights" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0a0a0a]/78" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-[#0a0a0a]/70" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-16 sm:pb-24 pt-32">
          <Reveal>
            <div className="flex items-center gap-4">
              <Eyebrow>Work · Client 02</Eyebrow>
            </div>
            <div className="mt-5 bg-white inline-flex p-3 sm:p-4">
              <img src={`${KK}/kotak-logo.jpg`} alt="Kotak Mahindra Bank" className="h-9 sm:h-12 w-auto object-contain" />
            </div>
            <h1 className="mt-7 font-display uppercase text-white text-6xl sm:text-8xl lg:text-[9rem] leading-[0.85] tracking-tight">
              Kotak
            </h1>
            <p className="mt-6 font-display uppercase text-3xl sm:text-5xl leading-none text-white">
              It started under lights.
            </p>
            <p className="mt-4 font-display text-2xl sm:text-3xl text-[#e62429]">2009</p>
          </Reveal>
        </div>
      </section>

      {/* FIRST BRIEF — D23 */}
      <section data-testid="kotak-firstbrief" className="border-t border-white/10 py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>The first brief</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95] max-w-4xl">
              Cricket was the brief.<br />Making it an occasion was the idea.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
            <Reveal><Chip src={`${KK}/clash-submit.jpg`} alt="Cricket Clash — submit your teams, 24th October" className="h-72 sm:h-96" /></Reveal>
            <Reveal delay={0.08}><Chip src={`${KK}/clash-lights.jpg`} alt="Cricket Clash — Intra Kotak Cricket Tournament, first time under lights, 24th October '09, Mumbai" className="h-72 sm:h-96" /></Reveal>
          </div>
          <Reveal className="mt-6"><p className="text-sm text-neutral-500 uppercase tracking-[0.2em]">Cricket Clash · 2009 · archival creative</p></Reveal>
        </div>
      </section>

      {/* FIRST NIGHT — D24 */}
      <section data-testid="kotak-firstnight" className="border-t border-white/10 py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-none">The first night. Under lights.</h2>
            <p className="mt-4 text-base sm:text-lg text-neutral-400">Central Railway Grounds · Mumbai · 2009</p>
          </Reveal>
          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
            {[
              ["night1.jpg", "Night cricket, wide"],
              ["night-a1.jpg", "Night cricket action"],
              ["trophies.jpg", "Trophies"],
              ["night2.jpg", "Night cricket, wide"],
              ["night-a2.jpg", "Night cricket action"],
            ].map(([f, alt], i) => (
              <Reveal key={f} delay={i * 0.05}>
                <img src={`${KK}/${f}`} alt={alt} loading="lazy" className="w-full h-auto border border-white/10" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BROADENED — D25 */}
      <section data-testid="kotak-broadened" className="border-t border-white/10 py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>The relationship broadened</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-none">Same sport. Different audience.</h2>
            <p className="mt-5 text-lg sm:text-xl text-neutral-300 max-w-2xl">Cricket moved beyond employees and into business communities.</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Reveal><img src={`${KK}/team.jpg`} alt="Kotak cricket team, archival" loading="lazy" className="w-full h-auto border border-white/10" /></Reveal>
              <Reveal delay={0.08}><img src={`${KK}/cricket-group.jpg`} alt="Kotak cricket group, archival" loading="lazy" className="w-full h-auto border border-white/10" /></Reveal>
            </div>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="border-l-2 border-[#e62429] pl-6">
                <p className="text-[#e62429] font-bold uppercase tracking-[0.2em] text-sm">Kotak IT Fest</p>
                <p className="mt-4 font-display uppercase text-white text-2xl sm:text-3xl leading-tight">One example of the format travelling beyond the original employee brief.</p>
                <p className="mt-4 text-base leading-relaxed text-neutral-400">The same sporting language could serve a different audience and purpose.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ORGANISATION CHANGED — D26 */}
      <section data-testid="kotak-orgchanged" className="border-t border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-[#0b2038] p-8 sm:p-16 lg:p-20 flex flex-col justify-center min-h-[60svh]">
            <Reveal>
              <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">Then the organisation changed.</h2>
              <p className="mt-5 inline-block bg-white text-[#0b2038] font-bold text-sm uppercase tracking-wide px-3 py-1">ING Vysya Bank merger</p>
              <p className="mt-8 font-display uppercase text-white text-2xl sm:text-4xl leading-tight">
                A larger footprint.<br />More people.<br />More places.
              </p>
            </Reveal>
          </div>
          <div className="p-8 sm:p-16 lg:p-20 flex flex-col justify-center min-h-[40svh]">
            <Reveal>
              <Eyebrow>Kona Kona Kotak</Eyebrow>
              <p className="mt-5 font-display uppercase text-white text-3xl sm:text-5xl leading-tight">
                The corporate proposition: presence in every corner.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROPOSITION → CRICKET FIELD — D27/D28 */}
      <section data-testid="kotak-proposition" className="border-t border-white/10 py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <Eyebrow>A corporate proposition</Eyebrow>
              <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">Walked onto a cricket field.</h2>
              <p className="mt-8 text-lg sm:text-xl text-[#5b9bd5] font-medium max-w-md">
                The proposition became a sporting property people could actually enter.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Chip src={`${KK}/kkk.webp`} alt="Kona Kona Kricket — grtr 2 b 30" className="h-56 sm:h-72" />
            </Reveal>
          </div>

          <Reveal className="mt-16">
            <div className="border-t border-white/12 pt-10 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <p className="text-[#5b9bd5] text-xs uppercase tracking-[0.25em] font-bold">Kona Kona Kotak</p>
                <p className="mt-2 font-display uppercase text-white text-2xl sm:text-3xl">Kotak in every corner</p>
              </div>
              <div>
                <p className="text-[#e62429] text-xs uppercase tracking-[0.25em] font-bold">Kona Kona Kricket</p>
                <p className="mt-2 font-display uppercase text-white text-2xl sm:text-3xl flex items-start gap-3">
                  <span className="text-[#e62429]">→</span> Cricket connects the dots
                </p>
              </div>
            </div>
            <div className="mt-8">
              <p className="font-display uppercase text-white text-xl sm:text-2xl">The business context came first.</p>
              <p className="mt-2 text-base sm:text-lg text-neutral-400">The sporting answer was designed around it.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PARTICIPATION — D29/D30 */}
      <section data-testid="kotak-participation" className="border-t border-white/10">
        <div className="relative isolate min-h-[70svh] flex items-end overflow-hidden">
          <img src={`${KK}/rustombaug.jpg`} alt="Kona Kona. Now playing — Rustom Baug, Mumbai" className="absolute inset-0 z-0 h-full w-full object-cover" />
          <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          <div className="relative z-10 mx-auto max-w-[1500px] w-full px-5 sm:px-8 pb-12">
            <Reveal>
              <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-none drop-shadow-lg">Kona Kona. Now playing.</h2>
              <p className="mt-3 text-[#e62429] font-bold uppercase tracking-[0.2em] text-sm">Rustom Baug · Mumbai</p>
            </Reveal>
          </div>
        </div>
        <div className="py-20 sm:py-28">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <h2 className="font-display uppercase text-white text-4xl sm:text-6xl leading-[0.95]">From proposition to participation.</h2>
              <p className="mt-6 text-lg sm:text-xl text-neutral-300 max-w-md">The property becomes real when people own the field.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <img src={`${KK}/celebration.jpg`} alt="Kotak team celebration with trophy" loading="lazy" className="w-full h-auto border border-white/10" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* IT SPREAD — D31 */}
      <section data-testid="kotak-spread" className="border-t border-white/10">
        <div className="bg-[#0b2038] py-24 sm:py-36">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.35em] text-white/60 font-bold">The relationship</p>
              <h2 className="mt-6 font-display uppercase text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.9]">
                Didn't just continue.
              </h2>
              <h2 className="mt-2 font-display uppercase text-[#e62429] text-5xl sm:text-7xl lg:text-8xl leading-[0.9]">
                It spread.
              </h2>
              <p className="mt-8 text-lg sm:text-2xl text-white/80">Across businesses. Across teams. Across briefs.</p>
            </Reveal>
          </div>
        </div>

        {/* ORGANISATIONAL SPREAD — D32 */}
        <div className="py-20 sm:py-28">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal>
              <Eyebrow>Organisational spread</Eyebrow>
              <h3 className="mt-5 font-display uppercase text-white text-3xl sm:text-5xl leading-none">Different parts of Kotak. Different briefs.</h3>
            </Reveal>
            <Reveal className="mt-6">
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-neutral-400 text-sm uppercase tracking-[0.2em]">
                <span>Soccer</span><span className="text-neutral-700">/</span>
                <span>Indoor Games</span><span className="text-neutral-700">/</span>
                <span>Table Tennis</span><span className="text-neutral-700">/</span>
                <span>Women</span>
              </div>
            </Reveal>
            <Reveal className="mt-8">
              <img src={`${KK}/posters.jpg`} alt="Kotak event creatives — Soccer, Indoor Games, Table Tennis, Women" loading="lazy" className="w-full h-auto border border-white/10 bg-white" />
            </Reveal>
          </div>
        </div>

        {/* SPORTING RESPONSE — D33 */}
        <div className="border-t border-white/10 py-20 sm:py-28">
          <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
            <Reveal>
              <Eyebrow>The brief expanded</Eyebrow>
              <h3 className="mt-5 font-display uppercase text-white text-3xl sm:text-5xl leading-none">So did the sporting response.</h3>
              <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-neutral-400 text-sm uppercase tracking-[0.2em]">
                <span>Badminton</span><span className="text-neutral-700">/</span>
                <span>Soccer</span><span className="text-neutral-700">/</span>
                <span>Cricket</span>
              </div>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {["day-action.jpg", "action.jpg", "participant.jpg"].map((f, i) => (
                <Reveal key={f} delay={i * 0.07}>
                  <img src={`${KK}/${f}`} alt="Kotak sport, archival" loading="lazy" className="w-full aspect-[4/3] object-cover border border-white/10" />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SCALE — D34 */}
      <section data-testid="kotak-scale" className="border-t border-white/10 py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>All India Cricket</Eyebrow>
            <h2 className="mt-5 font-display uppercase text-white text-4xl sm:text-6xl leading-none">Scale became part of the brief.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 font-bold">Cities</p>
              <ul className="mt-5 grid grid-cols-2 gap-x-8 gap-y-2">
                {["Mumbai","Pune","Ahmedabad","Delhi","Kolkata","Chennai","Hyderabad","Bengaluru"].map((c) => (
                  <li key={c} className="text-lg sm:text-xl text-neutral-300 border-b border-white/[0.07] py-2">{c}</li>
                ))}
              </ul>
            </Reveal>
            <div className="lg:col-span-7 space-y-8">
              {[["8","Cities"],["~132","Teams"],["1,400+","Employees"]].map(([n, l], i) => (
                <Reveal key={l} delay={i * 0.08}>
                  <div className="flex items-baseline gap-6 border-b border-white/10 pb-6">
                    <span className="font-display text-[#e62429] text-6xl sm:text-8xl leading-none tabular-nums">{n}</span>
                    <span className="font-display uppercase text-white text-2xl sm:text-3xl">{l}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTINUITY — D35 */}
      <section data-testid="kotak-continuity" className="border-t border-white/10 py-20 sm:py-28">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
            <Reveal className="relative">
              <img src={`${KK}/night-a1.jpg`} alt="2009, night cricket" loading="lazy" className="w-full h-full object-cover border border-white/10" />
              <span className="absolute top-3 left-3 bg-[#e62429] text-white text-xs font-bold px-2 py-1">2009</span>
            </Reveal>
            <Reveal delay={0.06}>
              <img src={`${KK}/trophies.jpg`} alt="Trophies" loading="lazy" className="w-full h-full object-cover border border-white/10" />
            </Reveal>
            <Reveal delay={0.12} className="relative">
              <img src={`${KK}/team2.jpg`} alt="2025/26, Kotak team" loading="lazy" className="w-full h-full object-cover border border-white/10" />
              <span className="absolute top-3 right-3 bg-[#e62429] text-white text-xs font-bold px-2 py-1">2025/26</span>
            </Reveal>
          </div>
          <Reveal className="mt-12 text-center">
            <p className="font-display uppercase text-white text-5xl sm:text-7xl leading-none tabular-nums">
              2009 <span className="text-[#e62429]">→</span> 2025/26
            </p>
            <p className="mt-6 text-lg sm:text-xl text-neutral-300 max-w-2xl mx-auto">
              From a first tournament to a sporting journey across Kotak.
            </p>
          </Reveal>
        </div>
      </section>

      <ChapterNav currentKey="kotak" />

      {/* Route back */}
      <section className="border-t border-white/10 py-16">
        <div className="mx-auto max-w-[1500px] px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-14">
          <Link to="/work" data-testid="kotak-back-work-bottom" className="group inline-flex items-center gap-3 font-display uppercase text-2xl sm:text-3xl text-white">
            <ArrowLeft className="h-6 w-6 text-[#e62429] group-hover:-translate-x-1 transition-transform" />
            Back to Work
          </Link>
          <Link to="/#gateways" data-testid="kotak-back-explore-bottom" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors">
            Back to Explore
            <ArrowUpRight className="h-4 w-4 opacity-40 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>
    </div>
  );
}
